/**
 * DAO Suite - Root Application Component
 *
 * Wraps the router with ErrorBoundary for consistent error handling.
 * Uses route-based code splitting for optimal performance.
 * Protected routes require authentication via ProtectedRoute wrapper.
 *
 * @see FAS-6.1 - Create and Deploy dao-suite
 */

import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Routes, Route } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, LoginRedirect } from '@hello-world-dao/auth';
import type { IcApiError } from '@hello-world-dao/auth';
import ErrorBoundary from './components/ErrorBoundary';
import { AppLayout } from './components/layout';
import { buildLoginFrontdoorUrl } from './services/identity-service-client';
import {
  runIdentityCodeBootstrap,
  resetIdentityCodeBootstrap,
  isIdentityBootstrapInFlight,
} from './services/identity-code-bootstrap';
import { getIdentityJwt } from './services/identity-jwt-store';
import { ToastContainer } from './components/ToastContainer';
import { OfflineBannerStandalone } from './components/OfflineBanner';
import { addToast } from './stores/atoms/toasts';
import {
  isPropertyGraduationViewEnabled,
  isSubmissionCaptureEnabled,
  isCredentialStatusEnabled,
} from './utils/featureFlags';

// Route-based code splitting - each page loads on demand
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const MembershipRenewal = lazy(() => import('./pages/MembershipRenewal'));
// BL-139: top-level /membership landing page with Pay-with-Card CTA.
const Membership = lazy(() => import('./pages/Membership'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const RenewalSuccess = lazy(() => import('./pages/RenewalSuccess'));
const RenewalCancel = lazy(() => import('./pages/RenewalCancel'));
const PaymentHistory = lazy(() => import('./pages/PaymentHistory'));
// Proposal routes (Governance)
const ProposalsPage = lazy(() => import('./pages/ProposalsPage'));
const CreateProposalPage = lazy(
  () => import('./features/proposal-creation/components/CreateProposalPage')
);
const SubmitConfirmation = lazy(
  () => import('./features/proposal-creation/components/SubmitConfirmation')
);
const ProposalDetailPage = lazy(() => import('./pages/ProposalDetailPage'));

// ADR-043 submission capture wizard (ae-001-4, feature-flag-gated — scope-stop
// until the envelope-ingest endpoint lands)
const SubmissionCapturePage = lazy(
  () => import('./features/submission-capture/SubmissionCapturePage')
);

// ae-001-6 — ADR-043 member credential-status surface (self-only read of the
// live attestation-record canister). Flag-gated OFF by default (scope-stop).
const CredentialStatusPage = lazy(
  () => import('./features/credential-status/CredentialStatusPage')
);

// Property status → proposal graduation (walkable-dao-001-9, feature-flag-gated)
const PropertyStatusPage = lazy(() => import('./pages/PropertyStatusPage'));

// Notifications
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

// Burn Donation
const BurnDonationPage = lazy(() => import('./pages/BurnDonationPage'));

// Escrow View
const EscrowViewPage = lazy(() => import('./pages/EscrowViewPage'));

// Member Directory
const MemberDirectoryPage = lazy(() => import('./pages/MemberDirectoryPage'));

// Member Profile (BL-023.2)
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage'));

// Own profile shortcut (BUG-DAO-127)
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

// Step-2 onboarding profile wizard (onb-001-6) — the II-link gate is
// unconditional (auth 2.0.0). Suite-local page; composes the shared
// useOnboardingGate predicate.
const ProfileWizardPage = lazy(() => import('./pages/ProfileWizardPage'));

// Step-1 onboarding II-link crossing (onb-001-10) — the browser proof-of-control
// gate. Rendered CHROMELESS (no AppLayout sidebar/header) but inside
// ProtectedRoute. Hard-gate: the only way into the portal for an unlinked member.
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

// Foreground verify-email step (bl-795) — gates AHEAD of the II-link crossing.
// Chromeless (like OnboardingPage) but inside ProtectedRoute. AppLayout's
// needsEmailVerification redirect routes unverified members here first.
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));

// Events Calendar (BL-025.3)
const EventsPage = lazy(() => import('./pages/EventsPage'));

// Airdrop Claim (AIRDROP-001.3)
const ClaimPage = lazy(() => import('./pages/ClaimPage'));

// Blog (BL-071.1, BL-071.2, BL-071.3)
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
// Blog editor — author/admin only (BL-071.3)
const BlogEditorPage = lazy(() => import('./pages/BlogEditorPage'));

// Unsubscribe (public page - no auth required)
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));

// Forgot-password (bl-777) — public redirect to the auth front-door's
// /forgot-password (the password-reset surface lives there, not in the portal).
const ForgotPasswordRedirect = lazy(() => import('./pages/ForgotPasswordRedirect'));

// Reverse FOS→DAO cross-domain SSO receiver (bl-1107-a). PUBLIC — it IS the
// auth-setting page (the member is not authenticated at this step), so it must
// render OUTSIDE AuthProvider/ProtectedRoute. Lands the "Switch to DAO" PKCE
// handoff (`?code=…#v=<verifier>`), exchanges, stores the JWT, then navigates to
// the portal where AuthProvider first mounts and derives the authed session.
const CrossDomainLoginPage = lazy(() => import('./pages/CrossDomainLoginPage'));

// KYC upgrade for legacy members (KYC-001.3 AC7)
const UpgradeMembership = lazy(() => import('./pages/UpgradeMembership'));

// Tax-form capture flow — W-9 / W-8BEN / W-8BEN-E (kyc-002-2). Post-KYC member
// onboarding: embedded Dropbox Sign e-sign, env-gated graceful degrade.
const TaxForms = lazy(() => import('./pages/TaxForms'));

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
    </div>
  );
}

// bl-977: shown when the pre-mount silent /token/refresh TRANSIENTLY failed
// (timeout / 5xx / exhausted connection-retries) rather than definitively 401'd.
// A member holding a valid refresh cookie must NOT be bounced to the sign-in shell
// on a transient blip, so we hold this neutral loader and offer a manual retry
// instead of mounting the (unauthenticated) auth tree.
//
// bl-977 regression #4: the hold is now BOUNDED — see TRANSIENT_ESCALATION_CAP_MS.
// After the wall-clock ceiling elapses still-transient, App escalates to a mounted
// (unauthenticated) tree so a mis-classified logged-out visitor reaches the login
// shell instead of an indefinite loader. The manual Retry remains live throughout.
//
// UX COPY + STYLING ARE PLACEHOLDER — the spinner-longer-vs-login-flash tradeoff is
// Menley's lane (bl-977 all-three review flag). Do not treat this copy as final.
function ReconnectingLoader({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen flex flex-col items-center justify-center gap-4 text-gray-600"
    >
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      <p data-testid="reconnecting-message">Reconnecting…</p>
      <button
        type="button"
        onClick={onRetry}
        data-testid="reconnecting-retry"
        className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        Retry
      </button>
    </div>
  );
}

type BootstrapUiState = 'resolving' | 'settled' | 'transient';

// bl-1475: the identity-jwt AuthProvider config shared by the protected subtree
// (BootGate, below — which adds the bl-1247 `isInitialSessionResolving` hold) and
// the PUBLIC `/login` route (which mounts it WITHOUT that hold — see the Routes).
//
// Why this exists: @hello-world-dao/auth@7.0.0 (bl-1442-e) repointed
// `LoginRedirect` off a context-free `checkSession()` cookie read onto
// `useAuth()` — the SAME JWT source `ProtectedRoute` reads. Its new contract
// REQUIRES being mounted inside an `<AuthProvider>`. `/login` sits in the bl-977-1
// public-routes block, OUTSIDE BootGate, so before bl-1475 a direct hit threw
// `useAuth must be used within an AuthProvider` → ErrorBoundary. Sharing one
// config keeps the two providers from drifting; `/login` deliberately omits
// `isInitialSessionResolving` (it needs a prompt redirect, not the member-reload
// hold — see the /login route comment).
//
// Built at RENDER TIME (a function, not a hoisted module constant) so
// `import.meta.env.*` is read at render — matching the pre-bl-1475 inline config
// and keeping the env values overridable in unit tests.
function buildAuthProviderConfig() {
  return {
    apiBaseUrl: import.meta.env.VITE_ORACLE_BRIDGE_URL,
    // dac-001-7: II-login now targets identity-service natively (POST /login/ii,
    // ES256 JWT, ic_principal-canonical per ADR-015) instead of oracle-bridge's
    // /api/auth/v2/ii-login. identityServiceUrl is REQUIRED in identity-jwt mode
    // (target of /token/exchange, /token/refresh, /logout); loginFrontDoorUrl is
    // set for completeness (this suite front-doors via LoginRedirect, not login()).
    authMode: 'identity-jwt' as const,
    identityServiceUrl: import.meta.env.VITE_IDENTITY_SERVICE_URL,
    loginFrontDoorUrl: import.meta.env.VITE_LOGIN_FRONTDOOR_URL,
    // authz-001-7a: activate the -4a JWT seam. authz-001-7b (auth@3.0.0) retired
    // the oracle-bridge cookie-session gate — the JWT is now the sole gate. When
    // a non-expired identity-service JWT is stored (by the pre-mount code→JWT
    // bootstrap, after the front-door `?code=`→exchange), AuthContext derives
    // isAuthenticated + roles from its claims; when null/expired the member is
    // unauthenticated (no cookie fallback). getIdentityJwt() reads the in-memory store.
    getIdentityJwt: () => getIdentityJwt(),
    e2eBypass: import.meta.env.VITE_E2E_AUTH_BYPASS === 'true',
    devBypass: import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',
    onIcUnavailable: (err: IcApiError) => {
      addToast(err.message, { type: 'warning', title: 'Network Issue', duration: 8000 });
    },
  };
}

// bl-977-1: the boot-verdict gate for MEMBER-ONLY routes only.
//
// bl-977 originally gated the ENTIRE router on the classified bootstrap: while it
// resolved (or held `transient`) App early-returned PageLoader / ReconnectingLoader
// BEFORE the router mounted, so even a route that never needs an authenticated
// session — /forgot-password, /privacy-policy, /unsubscribe, /login — was blocked
// behind auth-boot resolution (up to TRANSIENT_ESCALATION_CAP_MS under load). An
// unauthenticated visitor to a public route saw "Reconnecting…" / a blank loader
// instead of the real content or redirect.
//
// This gate is now scoped: the public routes render immediately (see App's Routes),
// and ONLY the protected subtree is wrapped in this layout element. It holds the
// loader states exactly as before, then — on a definitive verdict — mounts
// AuthProvider around the protected <Outlet/>. AuthProvider must NOT mount before
// the verdict settles: it derives auth state once at mount from the in-memory JWT
// store, so mounting it before the pre-mount bootstrap has populated the JWT would
// flash an authenticated member as logged-out. Public routes carry that constraint
// (they need neither AuthProvider context nor a resolved session), which is exactly
// why they sit OUTSIDE this gate.
function BootGate({
  bootstrapState,
  onReconnectRetry,
}: {
  bootstrapState: BootstrapUiState;
  onReconnectRetry: () => void;
}) {
  if (bootstrapState === 'resolving') {
    return <PageLoader />;
  }

  if (bootstrapState === 'transient') {
    return <ReconnectingLoader onRetry={onReconnectRetry} />;
  }

  return (
    <AuthProvider config={{
      // bl-1475: base identity-jwt config (see buildAuthProviderConfig above),
      // shared with the public /login provider.
      ...buildAuthProviderConfig(),
      // bl-1247: hold the unauthenticated verdict while the pre-mount silent
      // /token/refresh is still genuinely in flight (bounded by
      // SESSION_RESOLVE_CEILING_MS). Without this the bl-977 hold in
      // @hello-world-dao/auth collapses to a single setTimeout(…, 0) and a member
      // holding a valid hwdao_refresh cookie is bounced to the login shell the
      // macrotask after the 6s escalation force-mounts the tree. `isInitialSessionResolving`
      // is a STABLE module-scope reference sourced from the bootstrap's OWN in-flight
      // flag (NOT bootstrapState — see the Trap-1 note at the predicate definition).
      // This hold is PROTECTED-tree only: the public /login provider omits it so an
      // unauthenticated visitor redirects promptly (bl-1475 AC2).
      isInitialSessionResolving,
    }}>
      {/* ErrorBoundary INSIDE AuthProvider so a crash in a member page is caught
          without unmounting AuthProvider itself — and, because it sits below the
          public routes + global chrome (toast / offline banner), a member-page
          crash never takes those down either. (bl-977-1: preserves the pre-refactor
          nesting where AuthProvider was the outermost wrapper around the routed
          ErrorBoundary.) */}
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </AuthProvider>
  );
}

// bl-977 regression #4 (2026-07-04): a HARD wall-clock ceiling on the transient
// "Reconnecting…" hold, measured from App mount (page load).
//
// The original bl-977 fix parked EVERY `transient` verdict on an INDEFINITE hold
// with a manual-only Retry. Under concurrent load (the very login-burst condition
// bl-977 fixed), a genuinely-logged-out visitor's `/token/refresh` can classify
// `transient` (timeout / 5xx) instead of a clean 401 — and then the visitor is
// stuck on the loader forever, never reaching the login shell (regression #4:
// 8 deterministic unauth-gating failures, "reconnecting…retry" captured where the
// login/forgot-password shell belonged). Pre-bl-977 they got the login shell
// instantly.
//
// So after this ceiling elapses with the bootstrap still parked in `transient`
// (or an in-flight retry still unresolved), we escalate to a mounted tree:
// AuthProvider mounts unauthenticated → ProtectedRoute lands the visitor on the
// login shell. This is a UI-VERDICT DOWNGRADE ONLY — it NEVER issues another
// `/token/refresh` (bl-789 reuse-safety is untouched; no extra token rotation).
//
// TRADEOFF (deliberate): a member holding a VALID refresh cookie who hits a real
// upstream outage lasting past this cap now also gets bounced to the login shell.
// That is strictly BETTER than pre-bl-977 (which bounced them INSTANTLY on the
// first blip) and is honestly the only safe terminal state — an indefinite hold
// is worse. The member keeps the manual Retry throughout the hold, and a fresh
// login re-establishes the session.
//
// VALUE (6s) — ruled by Menley, AC8 UX review (bl-977-2, MENLEY-RESPONSE-2026-10-16
// §bl-977, answering COBY-RESPONSE-2026-08-07's measurement). Lowered from 12s.
// Menley's call: "when we do spin, 6 s not 12–14 s" — a single, short, honest
// member-visible hold. 6s is the whole transient budget now: the member sees the
// "Reconnecting…" loader for at most ~6s before either the session rehydrates or
// they are bounced to the login shell.
//
// The 6s cap sits comfortably BELOW the tightest unauth-gating regression predicate
// (15s; membership-profile TC-PROF-007 / TC-NOTIF-004), so the mounted ProtectedRoute
// fallback ("Checking authentication…") / login bounce reliably lands inside that
// assertion window (6s ≪ 15s — far more margin than the prior 12s).
//
// HONEST TRADEOFF (unchanged in kind, widened in degree from the 12s cap): the shared
// boundedRefresh state machine (@hello-world-dao/auth; DEFAULT_TIMEOUT_MS=8000,
// DEFAULT_MAX_RETRIES=2, backoff 400ms→800ms) has a conservative worst-case bound of
// ~9.2s (400 + 800 + 8000) and a full worst-case of ~25.2s (8000×3 + 400 + 800). 6s
// sits BELOW even the conservative bound, so a member holding a valid refresh cookie
// whose reconnect is still legitimately in flight at 6s is pre-empted and bounced to
// the login shell. Menley accepted this explicitly: a slow reconnect that has not
// landed within 6s is bounced to login (a fresh sign-in re-establishes the session) —
// which is still strictly better than pre-bl-977 (instant bounce on the first blip),
// and an indefinite hold is worse. A refresh that SUCCEEDS before 6s is still honored
// via the normal bootstrap→settled→authed-mount path (the escalation timer below sees
// `settled` and no-ops); only a refresh still genuinely in-flight AT 6s is bounced.
const TRANSIENT_ESCALATION_CAP_MS = 6_000;

// bl-1247: the ceiling on the AUTH VERDICT (distinct from TRANSIENT_ESCALATION_CAP_MS
// above, which un-parks the UI). Before bl-1247 the portal never passed
// `isInitialSessionResolving` to AuthProvider, so the bl-977 hold collapsed to a
// single `setTimeout(…, 0)`: at the escalation cap the tree force-mounted on an empty JWT
// store and ProtectedRoute bounced a member whose slow-but-succeeding `/token/refresh`
// was still in flight. Wiring the predicate (below) SPLITS mounting from deciding —
// the tree still mounts at the cap, but the unauthenticated verdict is deferred while
// the refresh is genuinely in flight. AuthContext's hold has no ceiling of its own
// (it re-arms a 50ms poll for as long as the predicate returns true), so the bound
// lives here.
//
// VALUE (6s) — ruled by Menley, AC8 UX review (bl-977-2). RECONCILED DOWN from 14s to
// EQUAL TRANSIENT_ESCALATION_CAP_MS. Coby's note (MENLEY-RESPONSE-2026-10-16 §bl-977):
// "the 14 was a test-budget number, not a member-experience one." Collapsing the two
// constants to the same value produces a SINGLE ~6s member-visible hold instead of the
// bl-1247 two-phase 12s-mount / 14s-decide split.
//
// CONSEQUENCE — the bl-1247 grace window is intentionally collapsed to ZERO. bl-1247
// split MOUNTING AuthProvider (at the 12s cap) from DECIDING its unauthenticated verdict
// (held until this 14s ceiling), so a slow-but-succeeding refresh landing in the 12–14s
// window was still honored. With cap == ceiling == 6s, the escalation force-mounts
// AuthProvider at the SAME instant this ceiling elapses, so `isInitialSessionResolving`
// (below) returns false at mount and the unauthenticated verdict commits at once — there
// is no post-mount grace. A refresh still in flight at 6s is bounced (the widened tradeoff
// documented on TRANSIENT_ESCALATION_CAP_MS above). The single 6s escalation cap is the
// SOLE terminal bound; this constant no longer EXTENDS the hold past the cap.
//
// Why keep it wired at all: the `isInitialSessionResolving` predicate still provides its
// own bounded-in-time guarantee (the `&& elapsed < SESSION_RESOLVE_CEILING_MS` clause
// returns false for every reachable input in bounded time, so the hold can never become
// an indefinite loader even if the two constants are later re-split), and keeping the
// bl-1247 mount-vs-decide machinery intact means re-splitting the caps in a future BL is
// a one-line constant change, not a re-derivation of the whole seam.
const SESSION_RESOLVE_CEILING_MS = 6_000;

// bl-1247: the page-load origin the SESSION_RESOLVE_CEILING_MS is measured from —
// the SAME origin as TRANSIENT_ESCALATION_CAP_MS (both armed at App mount, below).
// Null until App mounts; a null origin means the session-resolve hold is not active.
let sessionResolveOriginMs: number | null = null;

// bl-1247: the predicate wired into AuthProvider (`isInitialSessionResolving`).
// Reports that the INITIAL SESSION REFRESH is still genuinely in flight so
// AuthContext holds its unauthenticated verdict instead of bouncing a member who
// holds a valid `hwdao_refresh` cookie whose refresh is merely slow.
//
// ⚠️ Source discipline (do NOT "simplify" this):
//   - It reads the bootstrap module's OWN in-flight flag (isIdentityBootstrapInFlight),
//     NOT `bootstrapState` / `bootstrapStateRef` / any BootGate value. The cap
//     escalation FORCE-SETS bootstrapState to 'settled' (that is the only thing that
//     mounts AuthProvider with an unsettled refresh in the first place), so a
//     bootstrapState-derived predicate would return false at exactly the moment the
//     hold is needed — reproducing the bug through a different door with a fix that
//     looks correct. (bl-1247 Trap 1.)
//   - It is NOT `bootstrapPromise != null` (that never clears → parks forever). The
//     flag settles by either outcome. (bl-1247 Trap 2 / rule 1.)
//   - It is a STABLE module-scope function reference so it does not tear down and
//     restart AuthContext's poll on every AuthProvider re-render. (bl-1247 Trap 3.)
// The `&& elapsed < SESSION_RESOLVE_CEILING_MS` clause is the terminal bound: for
// every reachable input this returns false in bounded time, so the hold cannot become
// an indefinite loader (which would be bl-977 regression #4 through a new door).
// bl-977-2: with SESSION_RESOLVE_CEILING_MS now reconciled EQUAL to the escalation
// cap (6s), AuthProvider only ever mounts at/after the ceiling elapses (the escalation
// force-mount, or an earlier definitive bootstrap verdict with the in-flight flag
// already cleared), so this predicate returns false every time it is consulted and no
// longer extends the hold — the single 6s cap is the sole terminal bound. The clause
// stays as the predicate's own bounded-in-time guarantee (see SESSION_RESOLVE_CEILING_MS).
function isInitialSessionResolving(): boolean {
  if (sessionResolveOriginMs === null) return false;
  return (
    isIdentityBootstrapInFlight() &&
    Date.now() - sessionResolveOriginMs < SESSION_RESOLVE_CEILING_MS
  );
}

export default function App() {
  // The identity-service bootstrap settles the session BEFORE the auth tree mounts,
  // because the JWT store is in-memory (empty on every fresh load) and AuthProvider
  // resolves auth state once at mount — the JWT must be present first.
  //
  // It runs on EVERY load, handling two cases (see services/identity-code-bootstrap):
  //   - authz-001-7a `?code=` login return → exchange the one-time code for a JWT
  //     first, otherwise ProtectedRoute's external redirect aborts the in-flight
  //     exchange (infinite login bounce).
  //   - plain reload (no `?code=`) → silent refresh from the httpOnly `hwdao_refresh`
  //     cookie so the session rehydrates instead of the member being logged out on
  //     every refresh.
  //
  // bl-977: the bootstrap now returns a CLASSIFIED verdict, not just done/undone.
  // While it resolves we hold PageLoader. On a definitive verdict (`authed` or
  // `unauthenticated`) we mount the auth tree (an unauthenticated verdict lets
  // ProtectedRoute redirect to the login shell — correct). On a `transient`
  // failure (timeout / 5xx / exhausted connection-retries) we hold a neutral
  // "Reconnecting…" loader with a manual retry instead of painting the sign-in
  // shell — a member with a valid refresh cookie is never bounced on a blip.
  const [bootstrapState, setBootstrapState] = useState<BootstrapUiState>('resolving');
  // Bumped by the manual retry to re-fire the bootstrap effect after the
  // single-flight is reset (StrictMode-safe: the initial mount does NOT reset, so
  // the double-invoked initial effect still shares the one singleton promise).
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  // E2E bypass (Playwright CI): ProtectedRoute already lets tests through without
  // a real session, and the local-preview env has no CORS-allowed identity-service
  // — the silent refresh throws, which would classify `transient` and hold the
  // Reconnecting loader forever. Under the bypass a transient verdict proceeds to
  // mount instead. Specs that DO need an authed session stub `/token/refresh` and
  // land `authed` before this mapping matters (blog-comments-rollback.spec relies
  // on the refresh still FIRING, so the bypass must not skip it — only remap the
  // transient verdict). Never true in a real build.
  const isE2EBypass = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

  // bl-977 review hardening: a synchronous (not React-state) lock so a retry
  // cannot be re-entered while the current bootstrap cycle (initial or a
  // prior retry) is still unsettled — see handleReconnectRetry below for why
  // this must be a ref, not just the `bootstrapState === 'transient'` render
  // gate the Retry button's visibility already relies on.
  const retryLockRef = useRef(false);

  // bl-977 regression #4: latch + state-mirror for the wall-clock escalation.
  //   - escalatedRef: once the ceiling fires we've forced a mounted tree; any
  //     LATER bootstrap resolution (e.g. a retry's `/token/refresh` that lands
  //     after the ceiling) must NOT override it — otherwise a late `transient`
  //     would un-mount the tree back to the Reconnecting hold, past the cap.
  //   - bootstrapStateRef mirrors the latest render's state so the fire-once
  //     escalation timer (created in a mount-only effect, without `bootstrapState`
  //     in its deps) can read the current value without re-arming on every change.
  const escalatedRef = useRef(false);
  const bootstrapStateRef = useRef<BootstrapUiState>(bootstrapState);
  bootstrapStateRef.current = bootstrapState;

  useEffect(() => {
    let cancelled = false;
    retryLockRef.current = true;
    void runIdentityCodeBootstrap().then((status) => {
      // Once the wall-clock ceiling has escalated, ignore a late resolution: the
      // tree is already (deliberately) mounted unauthenticated — a trailing
      // `transient` must not bounce it back to the hold.
      if (cancelled || escalatedRef.current) return;
      retryLockRef.current = false;
      setBootstrapState(status === 'transient' && !isE2EBypass ? 'transient' : 'settled');
    });
    return () => {
      cancelled = true;
    };
  }, [bootstrapAttempt, isE2EBypass]);

  // bl-977 regression #4: the HARD wall-clock ceiling (see
  // TRANSIENT_ESCALATION_CAP_MS above). Mount-only, single-flight: one timer per
  // page load. It fires exactly once and force-settles ONLY if the app is still
  // parked pre-mount (either holding `transient` or resolving an in-flight retry) —
  // if a definitive verdict already mounted the tree (`settled`), it is a no-op.
  // StrictMode's double-invoked effect is safe: the cleanup clears the first
  // timer, leaving a single live timer, and the escalation is idempotent
  // (escalatedRef latch + a same-value setState). It NEVER calls
  // resetIdentityCodeBootstrap / runIdentityCodeBootstrap — no token rotation
  // (bl-789 untouched); it is a pure UI-verdict downgrade.
  useEffect(() => {
    // bl-1247: arm the auth-verdict ceiling from the SAME page-load origin as the
    // escalation cap, so `isInitialSessionResolving` measures its bound from mount.
    sessionResolveOriginMs = Date.now();
    const timer = setTimeout(() => {
      if (escalatedRef.current || bootstrapStateRef.current === 'settled') return;
      escalatedRef.current = true;
      setBootstrapState('settled');
    }, TRANSIENT_ESCALATION_CAP_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleReconnectRetry = useCallback(() => {
    // bl-789 hardening (code-level, not just render-timing): refuse to start a
    // retry while the current bootstrap cycle is still unsettled. Two
    // concurrent `/token/refresh` calls would each try to rotate the SAME
    // httpOnly refresh cookie; the loser presents an already-consumed token →
    // `ReuseDetected` → the WHOLE token family is revoked (hard logout
    // everywhere) — strictly worse than the transient loader this is meant to
    // resolve. The Retry button is only rendered once the CURRENT cycle has
    // already settled to `transient`, so this is normally unreachable through
    // the UI — but that reachability argument rests on React's render timing,
    // not on code. `retryLockRef` is a synchronous flag (unlike `useState`, it
    // can't be "batched away"), so two invocations landing in the same tick —
    // e.g. a fast double-click, or a future second retry entry point — still
    // only ever start ONE cycle.
    if (retryLockRef.current) return;
    retryLockRef.current = true;
    // NB: this deliberately does NOT reset the wall-clock escalation ceiling
    // (TRANSIENT_ESCALATION_CAP_MS is absolute from page load). A member may
    // Retry within the window, but a retry that itself re-lands `transient` does
    // not push the deadline out — otherwise a stuck upstream could hold the loader
    // forever behind repeated clicks. The ceiling is the terminal guarantee.
    // Clear the settled `transient` single-flight so the next run re-attempts the
    // silent refresh, then re-fire the effect. (Reset is only ever called on an
    // explicit user retry — never on the initial mount — so the StrictMode
    // single-flight guarantee is preserved.)
    resetIdentityCodeBootstrap();
    setBootstrapState('resolving');
    setBootstrapAttempt((n) => n + 1);
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ================================================================
                Public routes — no authentication required, and NOT gated on the
                boot verdict (bl-977-1). These render immediately on any load, so
                an unauthenticated visitor is never blocked behind auth-boot
                resolution (the "Reconnecting…"/blank-loader hold that could last
                up to TRANSIENT_ESCALATION_CAP_MS under staging load). They need no
                resolved session. (bl-1475: /login below is the one exception that
                needs AuthProvider *context* — but still NOT the BootGate hold.)
                ================================================================ */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {/* bl-1475: LoginRedirect (auth@7.0.0) derives its verdict from
                useAuth(), so it MUST be mounted inside an <AuthProvider> — without
                one a direct hit to /login threw `useAuth must be used within an
                AuthProvider` → ErrorBoundary "Something went wrong" (no email form).
                Wrap it in the SHARED identity-jwt config (buildAuthProviderConfig)
                so it has context, but do NOT put it behind BootGate and do NOT pass
                `isInitialSessionResolving`: /login stays PUBLIC and an unauthenticated
                visitor gets a prompt verdict → immediate redirect to the front door,
                with no boot-hold (bl-977-1 / bl-1475 AC2). On a fresh load the
                in-memory JWT store is empty, so AuthProvider resolves unauthenticated
                at once and LoginRedirect redirects. */}
            <Route path="/login" element={
              <AuthProvider config={buildAuthProviderConfig()}>
                <LoginRedirect
                  loginUrl={buildLoginFrontdoorUrl()}
                  fallbackPath="/dashboard"
                />
              </AuthProvider>
            } />
            {/* bl-777: the portal's /forgot-password used to render an empty body
                (no matching route). The password-reset surface lives on the auth
                front-door (registration-suite) alongside /login — and that's where
                identity-service emails the reset link — so redirect there.
                bl-977-1: session-agnostic (a password-reset request is FOR someone
                who can't get in), so it must render pre-verdict — not behind the
                same bootstrap that protects member-only routes. */}
            <Route path="/forgot-password" element={<ForgotPasswordRedirect />} />

            {/* bl-1107-a: reverse FOS→DAO "Switch to DAO" cross-domain SSO
                receiver. PUBLIC + session-agnostic — it is the page that ESTABLISHES
                the session (reads the PKCE `?code=…#v=<verifier>` handoff, exchanges,
                stores the JWT), so it renders pre-verdict and OUTSIDE the BootGate /
                AuthProvider subtree, exactly like /login. On success it navigates to
                the portal (authed via the stored JWT); on any failure it navigates
                to the portal unauthenticated → ProtectedRoute → login shell. */}
            <Route path="/auth/cross-domain-login" element={<CrossDomainLoginPage />} />

            {/* ================================================================
                Member-only routes — gated on the boot verdict + AuthProvider.
                BootGate holds PageLoader / ReconnectingLoader until the classified
                bootstrap settles, then mounts AuthProvider around this <Outlet/>.
                (bl-977-1: scoped from an app-wide early-return down to this subtree
                so it no longer blocks the public routes above.)
                ================================================================ */}
            <Route element={<BootGate bootstrapState={bootstrapState} onReconnectRetry={handleReconnectRetry} />}>
            {/* onb-001-10: chromeless Step-1 II-link crossing. Protected (authed
                member required) but rendered OUTSIDE AppLayout — no member
                sidebar/header — so an unlinked member never sees portal chrome
                before crossing. AppLayout's loop-fix redirects needsOnboarding
                members HERE; this route must NOT itself be under AppLayout or the
                redirect would loop. */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute
                  loginUrl={buildLoginFrontdoorUrl()}
                  redirectBehavior="external"
                  fallback={
                    <div
                      role="status"
                      aria-live="polite"
                      className="min-h-screen flex items-center justify-center text-gray-600"
                    >
                      Checking your sign-in…
                    </div>
                  }
                >
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* bl-795: chromeless foreground verify-email step. Gates AHEAD of the
                II-link crossing — an authenticated member whose email_verified is
                false lands here first (oracle-bridge member-data rejects an
                unverified bearer, so II-link → profile would dead-end on a 401).
                Protected + OUTSIDE AppLayout, exactly like /onboarding, so the
                AppLayout needsEmailVerification redirect targets a route that is
                NOT itself under AppLayout (no loop). */}
            <Route
              path="/onboarding/verify-email"
              element={
                <ProtectedRoute
                  loginUrl={buildLoginFrontdoorUrl()}
                  redirectBehavior="external"
                  fallback={
                    <div
                      role="status"
                      aria-live="polite"
                      className="min-h-screen flex items-center justify-center text-gray-600"
                    >
                      Checking your sign-in…
                    </div>
                  }
                >
                  <VerifyEmailPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes - require authentication, wrapped with AppLayout (sidebar + header + Outlet) */}
            {/*
              BL-294: The `fallback` below is intentionally explicit. While the
              async session check is in flight, ProtectedRoute renders this
              fallback — NOT the dashboard contents. This prevents an
              anonymous visitor from ever seeing authenticated UI, and keeps
              regression parity with the "gated UI" branch of the
              unauthenticated-portal-visit test (see
              testing/regression/dao/membership/dashboard.spec.ts). Once the
              session check resolves as unauthenticated, the component's
              effect redirects to the external login URL.
            */}
            <Route element={
              <ProtectedRoute
                loginUrl={buildLoginFrontdoorUrl()}
                redirectBehavior="external"
                fallback={
                  <div
                    role="status"
                    aria-live="polite"
                    className="min-h-screen flex items-center justify-center text-gray-600"
                  >
                    Checking your sign-in…
                  </div>
                }
              >
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/settings" element={<Settings />} />
              {/* bl-1549: /kyc was a stale Persona MOCK; the real verify surface is
                  /upgrade-membership (Didit-backed). Route + page removed so there is
                  one canonical verify path. A stale /kyc deep-link now hits the
                  catch-all → /dashboard. */}
              {/* KYC-001.3 AC7: legacy member upgrade path using Didit widget */}
              <Route path="/upgrade-membership" element={<UpgradeMembership />} />
              {/* kyc-002-2: W-9 / W-8BEN / W-8BEN-E tax-form capture flow */}
              <Route path="/tax-forms" element={<TaxForms />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/membership/renewal" element={<MembershipRenewal />} />
              <Route path="/membership/payment-history" element={<PaymentHistory />} />
              <Route path="/renewal/success" element={<RenewalSuccess />} />
              <Route path="/renewal/cancel" element={<RenewalCancel />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              {/* Proposal routes */}
              <Route path="/proposals" element={<ProposalsPage />} />
              <Route path="/proposals/create" element={<CreateProposalPage />} />
              <Route path="/proposals/draft/:draftId/edit" element={<CreateProposalPage />} />
              <Route path="/proposals/draft/:draftId/confirm" element={<SubmitConfirmation />} />
              <Route path="/proposals/:proposalId" element={<ProposalDetailPage />} />
              {/* ADR-043 submission capture wizard (ae-001-4). Flag-gated
                  (scope-stop, AI-P4-04): OFF until the envelope-ingest endpoint
                  lands; when off the route is unregistered and an unknown path
                  falls through to the catch-all → /dashboard. */}
              {isSubmissionCaptureEnabled() && (
                <Route path="/submit" element={<SubmissionCapturePage />} />
              )}
              {/* ADR-043 credential-status surface (ae-001-6). Flag-gated
                  (scope-stop): OFF until the alpha lights it up; when off the
                  route is unregistered and an unknown path falls through to the
                  catch-all → /dashboard. Self-only read of the live
                  attestation-record canister; fail-closed "not verified". */}
              {isCredentialStatusEnabled() && (
                <Route path="/credentials" element={<CredentialStatusPage />} />
              )}
              {/* Notifications */}
              <Route path="/notifications" element={<NotificationsPage />} />
              {/* Burn Donation */}
              <Route path="/burn-donation" element={<BurnDonationPage />} />
              {/* Escrow View */}
              <Route path="/escrow" element={<EscrowViewPage />} />
              {/* Member Directory */}
              <Route path="/members" element={<MemberDirectoryPage />} />
              <Route path="/members/:principal" element={<MemberProfilePage />} />
              {/* Own-profile shortcut (BUG-DAO-127) */}
              <Route path="/profile" element={<ProfilePage />} />
              {/* Step-2 onboarding profile wizard (onb-001-6) — the II-link
                  gate is unconditional (auth 2.0.0). */}
              <Route path="/onboarding/profile" element={<ProfileWizardPage />} />
              {/* Airdrop Claim (AIRDROP-001.3) */}
              <Route path="/claim" element={<ClaimPage />} />
              {/* Property status → proposal graduation (walkable-dao-001-9).
                  Feature-flag-gated until the backend owner-scoped registry read
                  edge lands (see propertyGraduationService). */}
              {isPropertyGraduationViewEnabled() && (
                <Route path="/property-status" element={<PropertyStatusPage />} />
              )}
              {/* Events Calendar (BL-168: /calendar redirects to /events) */}
              {/* BL-360: /events/:id deep-links into EventsPage with the detail modal auto-opened */}
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventsPage />} />
              <Route path="/calendar" element={<Navigate to="/events" replace />} />
              {/* Blog (BL-071.1, BL-071.2, BL-071.3) */}
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/editor/new" element={<BlogEditorPage />} />
              <Route path="/blog/editor/:id" element={<BlogEditorPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              {/* bl-1447: catch-all fallback. An unmatched path (removed route,
                  typo, stale bookmark) previously made <Routes> return null → a
                  blank body that hangs forever (this is what turned bl-1401's
                  /search removal into a 20s regression hang). Placed INSIDE the
                  AppLayout/ProtectedRoute group so an authed member's unknown
                  path redirects home inside the shell, while an unauthenticated
                  unknown path still routes through BootGate/ProtectedRoute to
                  the login shell exactly as before. */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
            </Route>
          </Routes>
        </Suspense>
        {/* Global toast container for notifications */}
        <ToastContainer position="top-right" />
        {/* Global offline indicator */}
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-auto max-w-md pointer-events-none">
          <div className="pointer-events-auto">
            <OfflineBannerStandalone />
          </div>
        </div>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
